import classNames from "classnames";
import React, { type JSX } from "react";
import SVG from "react-inlinesvg";
import { Key } from "ts-key-enum";
import { c, t } from "ttag";

import iconLiveChatDark from "../../../../img/icons/live-chat-dark.svg";
import iconXClose from "../../../../img/icons/x-close.svg";
import { RichText } from "../../../common/RichText";
import { focusElement, trapFocus } from "../../../utils/keyboardFocus";
import { MOBILE_WIDTH_THRESHOLD_ALTERNATIVE } from "../constants";
import { ConfiguratorPanelHeadAlternative } from "./ConfiguratorPanelHeadAlternative";
import { showFinanceBlock } from "./ConfiguratorPanelTrigger";

import styles from "./ConfiguratorPanel.module.scss";

interface IProps {
    prefixNameCapped: string;
    panelClass: string;
    onClosePanel: () => void;
    radioSelector: JSX.Element | null;
    chatLinkComponent?: JSX.Element | null;
    liveChatHeader?: string;
    enableChat?: boolean;
    isPlpPanelStyle?: boolean;
}

interface IState {
    closePanel: boolean;
}

// TODO(ssr, LEVEL 9 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 9/10 due to 284 LOC, complex lifecycle with componentDidMount/componentWillUnmount, focus trap management with cleanup, multiple refs (optionPanel, closeButton, triggerElement), scroll event listeners, dynamic height calculations, body class manipulation, keyboard event handling (Escape and Enter), and complex panel open/close state coordination.
export class ConfiguratorPanel extends React.PureComponent<IProps, IState> {
    private optionPanel: HTMLDivElement | null = null;
    private closeButton: HTMLButtonElement | null = null;
    private trapFocusCleanup: (() => void) | null = null;
    private triggerElement: HTMLElement | null = null; // Store the trigger

    public state: IState = {
        closePanel: false,
    };

    private readonly onEscKeyDown = (
        e: React.KeyboardEvent<HTMLDivElement>,
    ) => {
        if (e.key === Key.Escape) {
            this.props.onClosePanel();
        }
    };

    private readonly isMobileWidth = () => {
        return window.innerWidth < MOBILE_WIDTH_THRESHOLD_ALTERNATIVE;
    };

    private readonly updateSelectorContainerHeight = () => {
        if (this.isMobileWidth()) {
            return;
        }
        // Subtract the scrollTop from the height of the header
        // to get the correct vertical viewport at the position where the mouse scroll is
        // when the configurator is not sticky
        const headerOffset =
            parseInt(
                getComputedStyle(document.documentElement).getPropertyValue(
                    "--configurator--header-offset",
                ),
                10,
            ) || 0;
        const footerOffset =
            parseInt(
                getComputedStyle(document.documentElement).getPropertyValue(
                    "--configurator--footer-offset",
                ),
                10,
            ) || 0;
        const isAboveHeaderOffset =
            document.documentElement.scrollTop <= headerOffset;
        if (this.optionPanel) {
            if (footerOffset) {
                if (isAboveHeaderOffset) {
                    this.optionPanel.style.height = `calc(calc(100vh - ${footerOffset}px) - ${
                        headerOffset - document.documentElement.scrollTop
                    }px`;
                }
            } else if (isAboveHeaderOffset) {
                this.optionPanel.style.height = `calc(100vh - ${
                    headerOffset - document.documentElement.scrollTop
                }px`;
            }
        }
    };

    private readonly addStaticToBody = () => {
        document.body.classList.add("static");
    };

    private readonly removeStaticFromBody = () => {
        document.body.classList.remove("static");
    };

    private readonly addPanelStateToBody = () => {
        document.body.classList.add("configurator-panel-open");
    };

    private readonly removePanelStateToBody = () => {
        // Wait a few seconds to remove inline style `position: fixed` before removing `.configurator-panel-open` from `<body>`.
        setTimeout(() => {
            document.body.classList.remove("configurator-panel-open");
        }, 500);
    };

    private readonly onCloseButtonClick = () => {
        this.setState(
            {
                closePanel: true,
            },
            () => {
                this.removeStaticFromBody();
                this.removePanelStateToBody();
            },
        );
        showFinanceBlock();
        this.props.onClosePanel();
        this.returnFocusToTrigger();
    };

    private readonly onMouseEnterPanel = () => {
        if (this.optionPanel) {
            this.addStaticToBody();
        }
    };

    private readonly onMouseLeavePanel = () => {
        if (this.optionPanel) {
            this.removeStaticFromBody();
        }
    };

    private readonly onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === Key.Escape) {
            this.props.onClosePanel();
            this.returnFocusToTrigger();
        } else if (e.key === "Enter") {
            // Handle Enter key on focused label
            const activeElement = document.activeElement;
            if (activeElement?.tagName === "LABEL") {
                // Trigger a click on the label to select the radio option
                (activeElement as HTMLElement).click();
                // The panel will likely close after selection, so return focus
                setTimeout(() => {
                    this.returnFocusToTrigger();
                }, 0);
            }
        }
    };

    private readonly returnFocusToTrigger = () => {
        // Return focus to the original trigger element
        if (this.triggerElement) {
            focusElement(this.triggerElement);
        }
    };

    private readonly setupFocusTrap = () => {
        if (this.optionPanel) {
            this.trapFocusCleanup = trapFocus(this.optionPanel);
        }
    };

    componentDidMount() {
        // Store the currently focused element as the trigger
        this.triggerElement = document.activeElement as HTMLElement;

        // Wait for animation completed
        setTimeout(() => {
            if (this.closeButton) {
                focusElement(this.closeButton);
            }
            if (this.optionPanel) {
                this.setupFocusTrap();
            }
        }, 500);

        if (this.isMobileWidth()) {
            this.addStaticToBody();
        }

        const isAltConfig = document.querySelector(
            ".alternative-design .product-hero__configurator",
        );
        if (!isAltConfig) {
            if (!this.isMobileWidth) {
                this.updateSelectorContainerHeight();
            }

            window.addEventListener(
                "scroll",
                this.updateSelectorContainerHeight,
            );
        }

        this.addPanelStateToBody();
    }

    componentWillUnmount() {
        // Clean up focus trap
        if (this.trapFocusCleanup) {
            this.trapFocusCleanup();
            this.trapFocusCleanup = null;
        }
        this.removeStaticFromBody();
        // Return focus to trigger when component unmounts
        this.returnFocusToTrigger();
    }

    render() {
        const panelClasses = classNames({
            configurator__panel: true,
            [styles.panel]: true,
            [`configurator__panel--${this.props.panelClass}`]: true,
        });
        return (
            <div
                aria-modal="true"
                aria-label={c("ConfiguratorPanel")
                    .t`${this.props.prefixNameCapped} option selection panel`}
                className={panelClasses}
                ref={(elem) => {
                    this.optionPanel = elem;
                }}
                role="dialog"
                onKeyDown={this.onKeyDown}
                onMouseEnter={this.onMouseEnterPanel}
                onMouseLeave={this.onMouseLeavePanel}
            >
                {this.props.isPlpPanelStyle ? (
                    <ConfiguratorPanelHeadAlternative
                        prefixNameCapped={this.props.prefixNameCapped}
                        onCloseButtonClick={this.onCloseButtonClick}
                        closeButtonRef={(elem) => (this.closeButton = elem)}
                    />
                ) : (
                    <div>
                        <h5>{c("ConfiguratorPanel")
                            .t`Select ${this.props.prefixNameCapped}`}</h5>
                        <button
                            ref={(elem) => {
                                this.closeButton = elem;
                            }}
                            aria-label={t`Close`}
                            onClick={this.onCloseButtonClick}
                        >
                            <SVG aria-hidden="true" src={iconXClose} />
                        </button>
                    </div>
                )}

                <div>
                    <div
                        className={styles.panelSelector}
                        data-body-scroll-lock-ignore="true"
                    >
                        {this.props.radioSelector}
                    </div>
                    {this.props.enableChat && (
                        <div className={styles.chatLink}>
                            <SVG aria-hidden="true" src={iconLiveChatDark} />
                            {this.props.liveChatHeader && (
                                <RichText
                                    html={this.props.liveChatHeader}
                                    tagName="h4"
                                />
                            )}
                            <small>{t`Contact a sleep expert`}</small>
                            <span onClick={this.onCloseButtonClick}>
                                {this.props.chatLinkComponent}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}
